import i18next from 'i18next';
import React from 'react';
import { between, down, only, up } from 'styled-breakpoints';
import styled from 'styled-components';

import { Retailer } from '@rebound/shared-models/retailer/retailer.model';

import ContentContainer from '../../../components/ContentContainer';
import Picture from '../../../components/Picture';
import InformationCard from './InformationCard';

export type LandingPageContentProps = {
  retailer?: Retailer;
  title: string;
  description: string;
  style?: React.CSSProperties;
  className?: string;
  form?: React.ReactNode;
  searchOrderJourney: boolean;
};

const LandingPageContent = ({
  retailer,
  title,
  description,
  style,
  className,
  form,
  searchOrderJourney,
}: LandingPageContentProps) => {
  const topLeftImageSrc = retailer?.theme.metadata.landingImageTopLeft;
  const bottomLeftImageSrc = retailer?.theme.metadata.landingImageBottomLeft;
  const topRightImageSrc = retailer?.theme.metadata.landingImageTopRight;
  const bottomRightImageSrc = retailer?.theme.metadata.landingImageBottomRight;
  const tableLeftImageSrc = retailer?.theme.metadata.landingImageTabletLeft;
  const tableTopRightImageSrc =
    retailer?.theme.metadata.landingImageTabletTopRight;
  const tableBottomRightImageSrc =
    retailer?.theme.metadata.landingImageTabletBottomRight;
  const currentLanguage = i18next.language;
  const languageKey = currentLanguage.slice(0, 2);
  const informationCardsToShow: {
    barColor: string;
    headline: string;
    content: string;
    linkText: string;
    linkUrl: string;
  }[] = [];
  retailer?.informationCards.forEach((card) => {
    informationCardsToShow.push({
      barColor: card.barColor,
      headline:
        card.headlineTranslations?.[languageKey] ||
        card.headlineTranslations?.en ||
        '',
      content:
        card.contentTranslations?.[languageKey] ||
        card.contentTranslations?.en ||
        '',
      linkText:
        card.linkTextTranslations?.[languageKey] ||
        card.linkTextTranslations?.en ||
        '',
      linkUrl: card.linkUrl,
    });
  });
  return (
    <Wrapper style={style} className={className}>
      <MainContent>
        <ContentContainer>
          <MainRowItem>
            <TextContentRow>
              <Title
                searchOrderJourney={searchOrderJourney}
                dangerouslySetInnerHTML={{ __html: title }}
              />
              <Description searchOrderJourney={searchOrderJourney}>
                {description}
              </Description>
              <FormContainer>
                <FormWrapper>{form as any}</FormWrapper>
              </FormContainer>
            </TextContentRow>
            <ImagesRow>
              <div>
                <TopLeftImageWrapper>
                  {topLeftImageSrc && <Picture src={topLeftImageSrc} alt="" />}
                </TopLeftImageWrapper>
                <BottomLeftImageWrapper>
                  {bottomLeftImageSrc && (
                    <Picture src={bottomLeftImageSrc} alt="" />
                  )}
                </BottomLeftImageWrapper>
              </div>
              <div>
                <TopRightImageWrapper>
                  {topRightImageSrc && (
                    <Picture src={topRightImageSrc} alt="" />
                  )}
                </TopRightImageWrapper>
                <BottomRightImageWrapper>
                  {bottomRightImageSrc && (
                    <Picture src={bottomRightImageSrc} alt="" />
                  )}
                </BottomRightImageWrapper>
              </div>
            </ImagesRow>
          </MainRowItem>
        </ContentContainer>
        <ImagesRowTable>
          <ImagesLeftColTable>
            {tableLeftImageSrc && <Picture src={tableLeftImageSrc} alt="" />}
          </ImagesLeftColTable>
          <ImagesRightColTable>
            {tableTopRightImageSrc && (
              <Picture src={tableTopRightImageSrc} alt="" />
            )}
            {tableBottomRightImageSrc && (
              <Picture src={tableBottomRightImageSrc} alt="" />
            )}
          </ImagesRightColTable>
        </ImagesRowTable>
      </MainContent>
      <ContentContainer>
        <InformationCardsWrapper>
          {informationCardsToShow.map((card) => (
            <InfoCard
              key={card.headline}
              barColor={card.barColor}
              content={card.content}
              headline={card.headline}
              linkText={card.linkText}
              linkUrl={card.linkUrl}
            />
          ))}
        </InformationCardsWrapper>
      </ContentContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 24px 20px;
  background-color: ${(props) => props.theme.colors.grayLight};
  flex: 1;
`;

const MainContent = styled.div`
  padding: 24px 0;
  display: flex;
  align-items: center;

  ${up('sm')} {
    align-items: flex-start;
    justify-content: center;
    padding: 64px 0 0;
    min-height: calc(100vh - 550px);
  }

  ${up('lg')} {
    align-items: center;
    padding: 0 140px;
  }

  ${between('sm', 'lg')} {
    min-height: auto;
    display: block;
  }
`;

const MainRowItem = styled.div`
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;

  ${up('lg')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
`;

const TextContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;

  ${up('sm')} {
    margin-bottom: 48px;
  }

  ${up('lg')} {
    display: grid;
    grid-template-columns: 0.75fr;
  }

  ${only('md')} {
    min-width: 760px;
  }
`;

const ImagesRow = styled.div`
  display: none;
  margin-left: auto;

  ${up('lg')} {
    display: flex;
  }

  @media (max-height: 750px) {
    max-width: 75%;
  }

  @media (min-height: 750px) and (max-height: 900px) {
    max-width: 85%;
  }
`;

const ImagesRowTable = styled.div`
  display: none;

  img {
    display: block;
  }

  ${up('sm')} {
    display: flex;
  }

  ${up('lg')} {
    display: none;
  }

  ${between('sm', 'md')} {
    width: calc(100% + 40px);
    transform: translateX(-20px);
  }
`;

const ImagesLeftColTable = styled.div`
  flex: 61.5% 1;
`;
const ImagesRightColTable = styled.div`
  flex: 29% 1;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1<{ searchOrderJourney: boolean }>`
  font-weight: bold;
  line-height: 40px;
  font-size: 36px;

  ${up('sm')} {
    text-align: left;
    font-size: 48px;
    line-height: 100%;
    ${({ searchOrderJourney }) => !searchOrderJourney && 'max-width: 450px'}
  }

  ${up('lg')} {
    text-align: left;
  }

  ${between('sm', 'lg')} {
    ${({ searchOrderJourney }) => searchOrderJourney && 'text-align: center;'}

    br {
      display: none;
    }
  }
`;

const Description = styled.p<{ searchOrderJourney: boolean }>`
  margin: 16px 0 20px;
  line-height: 24px;

  ${up('sm')} {
    text-align: left;
  }

  ${up('lg')} {
    text-align: left;
    max-width: 420px;
  }

  ${between('sm', 'lg')} {
    ${({ searchOrderJourney }) => searchOrderJourney && 'text-align: center;'}
  }
`;

const FormContainer = styled.div`
  ${up('sm')} {
    display: flex;
    flex-direction: column;
  }

  ${up('lg')} {
    width: auto;
    display: block;
  }
`;

const FormWrapper = styled.div`
  max-width: 370px;

  ${up('xs')} {
    width: auto;
    max-width: 100%;
  }

  ${up('lg')} {
    max-width: 370px;
  }
`;

const TopLeftImageWrapper = styled.div`
  margin-top: 50px;
  padding: 9px;
  box-shadow: border-box;
`;

const BottomLeftImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
`;

const TopRightImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
  overflow: hidden;
`;

const BottomRightImageWrapper = styled.div`
  padding: 9px;
  box-shadow: border-box;
  overflow: hidden;
`;

const InformationCardsWrapper = styled.div`
  display: flex;
  justify-content: center;

  ${down('sm')} {
    flex-direction: column;
    align-items: center;
  }

  ${up('sm')} {
    flex-direction: row;
    align-items: stretch;
    margin-top: 20px;
  }

  ${between('sm', 'md')} {
    margin-top: -25px;
    position: relative;
    z-index: 2;
  }
`;

const InfoCard = styled(InformationCard)`
  box-shadow:
    0px 10px 20px rgba(0, 0, 0, 0.04),
    0px 2px 6px rgba(0, 0, 0, 0.04),
    0px 0px 1px rgba(0, 0, 0, 0.04);

  ${down('sm')} {
    margin: 0 0 24px 0;
  }

  ${up('sm')} {
    margin: 0 10px;
  }
`;

export default LandingPageContent;
